import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import pb from '../lib/pocketbaseClient';

export const AccountsProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    clientId: '',
    type: 'construction',
    status: 'ongoing',
    workOrderValue: '',
    startDate: '',
    expectedEndDate: '',
    actualEndDate: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientDetails, setClientDetails] = useState(null);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const fetchClients = async () => {
      try {
        if (!pb.authStore.isValid) {
          if (isMounted.current) {
            setError('Please login to fetch clients.');
            setLoadingClients(false);
          }
          return;
        }
        const records = await pb.collection('leads').getFullList({ sort: 'name' });
        if (isMounted.current) {
          setClients(records);
          if (records.length === 0) {
            setError('No clients found in leads collection.');
          } else {
            setError(''); // clear any previous error
          }
        }
      } catch (err) {
        // Ignore cancellation errors (they happen on unmount)
        if (err.name === 'AbortError' || err.message?.includes('aborted')) {
          return;
        }
        if (isMounted.current) {
          console.error('Error fetching clients:', err);
          setError('Could not fetch clients. Please try again.');
        }
      } finally {
        if (isMounted.current) {
          setLoadingClients(false);
        }
      }
    };

    fetchClients();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Auto-populate location from client address
  useEffect(() => {
    if (formData.clientId) {
      const fetchClientDetails = async () => {
        try {
          const client = await pb.collection('leads').getOne(formData.clientId);
          setClientDetails(client);
          if (client.address) {
            setFormData(prev => ({ ...prev, location: client.address || '' }));
          }
        } catch (err) {
          console.warn('Could not fetch client details:', err);
          setClientDetails(null);
        }
      };
      fetchClientDetails();
    } else {
      setClientDetails(null);
    }
  }, [formData.clientId]);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        location: project.location || '',
        clientId: project.clientId || '',
        type: project.type || 'construction',
        status: project.status || 'ongoing',
        workOrderValue: project.workOrderValue || '',
        startDate: project.startDate || '',
        expectedEndDate: project.expectedEndDate || '',
        actualEndDate: project.actualEndDate || '',
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        name: formData.name,
        location: formData.location || '',
        clientId: formData.clientId || '',
        type: formData.type,
        status: formData.status,
        workOrderValue: parseFloat(formData.workOrderValue) || 0,
        startDate: formData.startDate || '',
        expectedEndDate: formData.expectedEndDate || '',
        actualEndDate: formData.actualEndDate || '',
      };

      // Check if collection exists
      try {
        await pb.collection('accounts_projects').getList(1, 1);
      } catch (checkErr) {
        if (checkErr.status === 404) {
          setError('Collection "accounts_projects" does not exist. Please create it in PocketBase Admin.');
          setLoading(false);
          return;
        }
      }

      if (project?.id) {
        await pb.collection('accounts_projects').update(project.id, data);
      } else {
        await pb.collection('accounts_projects').create(data);
      }
      onSave();
    } catch (err) {
      const msg = err.message || err.data?.message || 'Failed to save project';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto shadow-xl">
      <CardContent className="p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-2">
          {project?.id ? '✏️ Edit Accounts Project' : '➕ New Accounts Project'}
        </h2>
        <p className="text-sm text-slate-500 mb-4">Manage construction &amp; interior projects (Accounts Dept.)</p>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Project Name *</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Alamgir Residence"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Auto-populated from client address"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
              <select
                name="clientId"
                value={formData.clientId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
                disabled={loadingClients}
              >
                <option value="">Select Client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name} {client.phone ? `(${client.phone})` : ''}
                  </option>
                ))}
              </select>
              {loadingClients && <span className="text-xs text-slate-400">Loading clients...</span>}
              {!loadingClients && clients.length === 0 && !error && (
                <span className="text-xs text-amber-500">No clients yet. Add some leads first.</span>
              )}
              {clientDetails && (
                <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                  <div>📧 {clientDetails.email || 'No email'}</div>
                  <div>📞 {clientDetails.phone || 'No phone'}</div>
                  <div>📍 {clientDetails.address || 'No address'}</div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                required
              >
                <option value="construction">🏗️ Construction</option>
                <option value="interior">🛋️ Interior</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                <option value="upcoming">🔜 Upcoming</option>
                <option value="ongoing">🟢 Ongoing</option>
                <option value="completed">✅ Completed</option>
                <option value="on-hold">⏸️ On Hold</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Work Order Value (₹)</label>
              <Input
                name="workOrderValue"
                type="number"
                value={formData.workOrderValue}
                onChange={handleChange}
                placeholder="e.g., 5000000"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <Input
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Expected End Date</label>
              <Input
                name="expectedEndDate"
                type="date"
                value={formData.expectedEndDate}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Actual End Date</label>
              <Input
                name="actualEndDate"
                type="date"
                value={formData.actualEndDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={loading}>
              {loading ? 'Saving...' : (project?.id ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
